import { useEffect, useCallback, useState, useRef, useMemo } from "react";

const useKeyboardShortcut = (
  callback = null,
  targetKeys = [],
  node = null,
  options = {
    enableKeyPress: true,
    trackPressedKeys: false,
    preventDefault: false,
    enableRepeat: true,
    throttleInterval: 80, // Default throttle interval in milliseconds
  }
) => {
  const [pressedKeyCodes, setPressedKeyCodes] = useState(new Map());
  const keyCodeToKeysRef = useRef(new Map());
  const keyRef = useRef(null);
  const keyCodeRef = useRef(null);
  const eventTriggerTimerRef = useRef(null);
  const lastCallTimeRef = useRef(null);

  const throttleInterval = useMemo(
    () => options.throttleInterval || 80,
    [options]
  );
  const enableRepeat = useMemo(() => options.enableRepeat !== false, [options]);
  const isTrackingPressedKeys = useMemo(
    () => options.trackPressedKeys === true,
    [options]
  );
  const isPreventDefault = useMemo(
    () => options.preventDefault === true,
    [options]
  );
  const isEnableKeyPress = useMemo(
    () => options.enableKeyPress !== false,
    [options]
  );

  // Throttled callback function
  const throttledCallback = useCallback(
    (event, callbackData) => {
      const now = Date.now();
      if (
        lastCallTimeRef.current &&
        now - lastCallTimeRef.current < throttleInterval
      ) {
        return;
      }
      lastCallTimeRef.current = now;
      callback(event, callbackData);
    },
    [callback, throttleInterval]
  );

  // Function to trigger callback with current state
  const triggerCallback = useCallback(
    (event, isRepeated = false) => {
      if (!callback) return;

      const callbackData = {
        key: keyRef.current,
        pressedKeys: Array.from(new Set(keyCodeToKeysRef.current.values())),
        keyCode: keyCodeRef.current,
        pressedKeyCodes: Array.from(keyCodeToKeysRef.current.keys()),
        isMultiKey: keyCodeToKeysRef.current.size > 1,
        isRepeated,
      };

      if (throttleInterval > 0) {
        // console.log("Triggering throttledCallback with data:", callbackData);
        throttledCallback(event, callbackData);
      } else {
        // console.log("Triggering callback with data:", callbackData);
        callback(event, callbackData);
      }
    },
    [callback]
  );

  const clearEventTriggerTimer = useCallback(() => {
    if (eventTriggerTimerRef.current) {
      // console.log(
      //   "Cleared event trigger timer. Pressed keys:",
      //   keyCodeToKeysRef.current
      // );
      clearInterval(eventTriggerTimerRef.current);
      eventTriggerTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isEnableKeyPress) return;

    const targetNode = node || document;

    // Function to clear all pressed keys when focus is lost
    const handleFocusLoss = () => {
      if (isTrackingPressedKeys) {
        keyCodeToKeysRef.current.clear();
        setPressedKeyCodes(new Map());
      }
      // Clear any running timers
      // console.log("Clearing event trigger timer on focus loss.");
      clearEventTriggerTimer();
    };

    const handleKeyDown = (event) => {
      const key = event.key;
      const keyCode = event.code; // Use keyCode for physical key tracking

      // Early return if keyboard events are disabled
      if (!isEnableKeyPress) {
        return;
      }

      // Skip if the event originated from a text input element
      const tag = event.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || event.target.isContentEditable) {
        return;
      }
      if (event.repeat) {
        // console.log("Ignoring repeated keydown event for:", key);
        return;
      }

      if (
        targetKeys.length > 0 &&
        !targetKeys.includes(keyCode) &&
        !targetKeys.includes(key)
      ) {
        // console.log("Key not in targetKeys, ignoring:", keyCode);
        return;
      }

      keyRef.current = key;
      keyCodeRef.current = keyCode;

      // Update list of currently pressed keys
      if (isTrackingPressedKeys) {
        const newPressedKeyCodes = new Map(keyCodeToKeysRef.current);
        newPressedKeyCodes.set(keyCode, key);
        keyCodeToKeysRef.current = newPressedKeyCodes;
        setPressedKeyCodes(newPressedKeyCodes);
      }

      if (isPreventDefault) {
        event.preventDefault();
      }

      // Call callback immediately for the first press (not repeated)
      triggerCallback(event, false);

      // Set up our own timer for repeated callbacks if enableRepeat is true
      if (enableRepeat && !eventTriggerTimerRef.current) {
        // console.log(
        //   "Setting up event trigger timer for key:",
        //   key,
        //   "pressedKeys:",
        //   keyCodeToKeysRef.current
        // );
        eventTriggerTimerRef.current = setInterval(() => {
          // console.log("repeated key interval for key:", key);
          if (keyCodeToKeysRef.current.size > 0) {
            // console.log("Triggering callback for repeated key press:", key);
            triggerCallback(event, true);
          } else {
            // console.log("No keys pressed, clearing event trigger timer.");
            clearEventTriggerTimer();
          }
        }, throttleInterval);
      }
    };

    const handleKeyUp = (event) => {
      const key = event.key;
      const keyCode = event.code; // Use keyCode for physical key tracking
      // console.log("keyup key:", key, "code:", keyCode);
      if (keyCodeRef.current != keyCode) {
        keyRef.current = null;
      }
      if (keyRef.current !== key) {
        keyRef.current = null;
      }

      if (isTrackingPressedKeys && keyCodeToKeysRef.current.has(keyCode)) {
        const newPressedKeyCodes = new Map(keyCodeToKeysRef.current);
        newPressedKeyCodes.delete(keyCode);
        keyCodeToKeysRef.current = newPressedKeyCodes;
        setPressedKeyCodes(newPressedKeyCodes);
        // Clear timer if no keys are pressed
        if (keyCodeToKeysRef.current.size === 0) {
          // console.log("Clearing event trigger timer on keyup: No keys pressed");
          clearEventTriggerTimer();
        }
      }
    };

    targetNode.addEventListener("keydown", handleKeyDown);
    if (isTrackingPressedKeys) {
      targetNode.addEventListener("keyup", handleKeyUp);
      // Clear pressed keys when window loses focus
      window.addEventListener("blur", handleFocusLoss);
      // Also clear when visibility changes (e.g., tab switching)
      document.addEventListener("visibilitychange", handleFocusLoss);
      // Clear pressed keys when context menu opens
      targetNode.addEventListener("contextmenu", handleFocusLoss);
    }

    return () => {
      targetNode.removeEventListener("keydown", handleKeyDown);
      if (isTrackingPressedKeys) {
        targetNode.removeEventListener("keyup", handleKeyUp);
        window.removeEventListener("blur", handleFocusLoss);
        document.removeEventListener("visibilitychange", handleFocusLoss);
        targetNode.removeEventListener("contextmenu", handleFocusLoss);
      }
      // Clean up timer on unmount
      keyCodeToKeysRef.current.clear();
      setPressedKeyCodes(new Map());
      // console.log("Cleaning up on unmount, clearing pressed keys and timer.");
      clearEventTriggerTimer();
    };
  }, [
    callback,
    targetKeys,
    node,
    isEnableKeyPress,
    isTrackingPressedKeys,
    isPreventDefault,
    enableRepeat,
    throttleInterval,
  ]);

  return {
    pressedKeys: Array.from(new Set(pressedKeyCodes.values())),
    pressedKeyCodes: Array.from(pressedKeyCodes.keys()),
    isKeyPressed: (key) => pressedKeyCodes.has(key),
    isAnyKeyPressed: () => pressedKeyCodes.size > 0,
    clearPressedKeys: () => {
      keyCodeToKeysRef.current.clear();
      setPressedKeyCodes(new Map());
      // Clear timer when manually clearing keys
      clearEventTriggerTimer();
    },
  };
};

export default useKeyboardShortcut;
